import { apiCall } from './api';

export type DashboardStats = {
  totalUsers: { value: number; delta: number };
  activeMiners: { value: number; delta: number };
  totalMined: { value: number; delta: number };
  totalReferrals: { value: number; delta: number };
};

export type SparklinePoint = {
  day: string;
  mined: number;
};

export type ActiveMiner = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  balance: number;
  totalMined: number;
  rate: number;
  startedAt: string;
  endsAt: string;
  secondsRemaining: number;
};

export async function fetchDashboard(token: string): Promise<{
  stats: DashboardStats;
  sparkline: SparklinePoint[];
  admin: { id: string; role: string };
}> {
  return apiCall('/admin/dashboard', { token });
}

export async function fetchActiveMiners(token: string): Promise<{
  miners: ActiveMiner[];
  count: number;
}> {
  return apiCall('/admin/active-miners', { token });
}

export type AdminUserRow = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  provider: string;
  role: 'user' | 'admin' | 'super_admin';
  balance: number;
  totalMined: number;
  streakDays: number;
  emailVerified: boolean;
  isMining: boolean;
  createdAt: string;
  lastLoginAt: string;
};

export async function fetchUsers(
  token: string,
  params: { search?: string; limit?: number; offset?: number } = {}
): Promise<{ users: AdminUserRow[]; total: number; limit: number; offset: number }> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  return apiCall(`/admin/users${qs ? '?' + qs : ''}`, { token });
}

export type UserDetail = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  provider: string;
  role: 'user' | 'admin' | 'super_admin';
  referralCode: string;
  balance: number;
  totalMined: number;
  miningRate: number;
  miningStartedAt: string | null;
  miningEndsAt: string | null;
  miningClaimed: boolean;
  streakDays: number;
  lastMinedAt: string | null;
  createdAt: string;
  emailVerified: boolean;
  bannedAt: string | null;
  bannedReason: string | null;
  referralsCount: number;
  sessionsCount: number;
  minedTotal: number;
};

export type UserActivity = {
  id: string;
  type: string;
  amount: number;
  description: string;
  createdAt: string;
};

export type UserSession = {
  id: string;
  startedAt: string;
  endsAt: string;
  claimedAt: string | null;
  rate: number;
  amount: number;
  status: string;
};

export type UserReferral = {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  emailVerified: boolean;
  rewarded: boolean;
};

export async function fetchUserDetail(token: string, userId: string): Promise<{
  user: UserDetail;
  activities: UserActivity[];
  sessions: UserSession[];
  referrals: UserReferral[];
}> {
  return apiCall(`/admin/users/${userId}`, { token });
}

export async function banUser(token: string, userId: string, reason: string) {
  return apiCall(`/admin/users/${userId}/ban`, {
    method: 'POST', token, body: { reason },
  });
}

export async function unbanUser(token: string, userId: string) {
  return apiCall(`/admin/users/${userId}/unban`, { method: 'POST', token });
}

export async function adjustBalance(
  token: string, userId: string, delta: number, reason: string
) {
  return apiCall(`/admin/users/${userId}/balance`, {
    method: 'POST', token, body: { delta, reason },
  });
}

export async function changeRole(token: string, userId: string, role: string) {
  return apiCall(`/admin/users/${userId}/role`, {
    method: 'POST', token, body: { role },
  });
}

export async function resetMining(token: string, userId: string) {
  return apiCall(`/admin/users/${userId}/reset-mining`, { method: 'POST', token });
}

export type ConfigItem = {
  key: string;
  value: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  description: string;
  updatedAt: string;
};

export type ConfigGroups = Record<string, ConfigItem[]>;

export async function fetchConfig(token: string): Promise<{ config: ConfigGroups }> {
  return apiCall('/admin/config', { token });
}

export async function updateConfig(token: string, key: string, value: string) {
  return apiCall(`/admin/config/${encodeURIComponent(key)}`, {
    method: 'PUT',
    token,
    body: { value },
  });
}

export type ContentPage = {
  id: string;
  slug: string;
  title: string;
  bodyMarkdown: string;
  locale: string;
  published: boolean;
  showInMenu: boolean;
  displayOrder: number;
  menuLocation: 'login' | 'profile_info' | 'profile_support' | 'none' | null;
  menuIcon: string | null;
  menuLabel: string | null;
  menuOrder: number;
  updatedAt: string;
};

export type ContentPageListItem = {
  id: string;
  slug: string;
  title: string;
  locale: string;
  published: boolean;
  showInMenu: boolean;
  displayOrder: number;
  updatedAt: string;
};

export async function fetchContentPages(token: string): Promise<{
  pages: ContentPageListItem[];
}> {
  return apiCall('/admin/content/pages', { token });
}

export async function fetchContentPage(token: string, id: string): Promise<{
  page: ContentPage;
}> {
  return apiCall(`/admin/content/pages/${id}`, { token });
}

export async function createContentPage(
  token: string,
  data: { slug: string; title: string; bodyMarkdown?: string }
): Promise<{ page: ContentPage }> {
  return apiCall('/admin/content/pages', {
    method: 'POST', token, body: data,
  });
}

export async function updateContentPage(
  token: string,
  id: string,
  data: {
    title?: string;
    bodyMarkdown?: string;
    published?: boolean;
    showInMenu?: boolean;
    displayOrder?: number;
  }
): Promise<{ page: ContentPage }> {
  return apiCall(`/admin/content/pages/${id}`, {
    method: 'PUT', token, body: data,
  });
}

export async function updateContentPageMenu(
  token: string,
  pageId: string,
  data: {
    show_in_menu: boolean;
    menu_location: string;
    menu_icon: string;
    menu_label: string;
    menu_order: number;
  }
) {
  return apiCall(`/admin/content/pages/${pageId}/menu`, {
    method: 'PUT',
    body: data,
    token,
  });
}

export async function deleteContentPage(token: string, id: string) {
  return apiCall(`/admin/content/pages/${id}`, { method: 'DELETE', token });
}

export type Announcement = {
  id: string;
  title: string;
  body: string;
  type: 'banner' | 'modal';
  actionUrl: string | null;
  actionLabel: string | null;
  backgroundColor: string;
  textColor: string;
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
};

export async function fetchAnnouncements(token: string): Promise<{
  announcements: Announcement[];
}> {
  return apiCall('/admin/content/announcements', { token });
}

export async function createAnnouncement(
  token: string,
  data: Partial<Announcement>
): Promise<{ announcement: Announcement }> {
  return apiCall('/admin/content/announcements', {
    method: 'POST', token, body: data,
  });
}

export async function updateAnnouncement(
  token: string,
  id: string,
  data: Partial<Announcement>
): Promise<{ announcement: Announcement }> {
  return apiCall(`/admin/content/announcements/${id}`, {
    method: 'PUT', token, body: data,
  });
}

export async function deleteAnnouncement(token: string, id: string) {
  return apiCall(`/admin/content/announcements/${id}`, {
    method: 'DELETE', token,
  });
}

export type EmailTemplate = {
  id: string;
  code: string;
  subject: string;
  bodyHtml: string;
  variables: string[];
  description: string | null;
  active: boolean;
  updatedAt: string;
};

export async function fetchEmailTemplates(token: string): Promise<{
  templates: EmailTemplate[];
}> {
  return apiCall('/admin/content/email-templates', { token });
}

export async function fetchEmailTemplate(token: string, id: string): Promise<{
  template: EmailTemplate;
}> {
  return apiCall(`/admin/content/email-templates/${id}`, { token });
}

export async function updateEmailTemplate(
  token: string,
  id: string,
  data: { subject?: string; bodyHtml?: string; active?: boolean }
): Promise<{ template: EmailTemplate }> {
  return apiCall(`/admin/content/email-templates/${id}`, {
    method: 'PUT', token, body: data,
  });
}

// ─── Push Notification ───────────────────────────────

export type BroadcastTarget = 'all' | 'segment' | 'user';

export type BroadcastSegment =
  | 'kyc_verified'
  | 'active_miners'
  | 'has_push_token';

export type BroadcastPayload = {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  target: BroadcastTarget;
  segment?: BroadcastSegment;
  userIds?: number[];
  channelId?: string;
};

export type BroadcastResult = {
  success: boolean;
  totalUsers: number;
  sent: number;
  failed: number;
  note?: string;
};

export async function sendPushBroadcast(
  token: string,
  payload: BroadcastPayload
): Promise<BroadcastResult> {
  return apiCall('/admin/push/broadcast', {
    method: 'POST',
    token,
    body: payload,
  });
}

export type ScheduleBroadcastPayload = BroadcastPayload & {
  scheduledAt: string;
};

export type ScheduleBroadcastResult = {
  success: boolean;
  jobId: number;
  scheduledFor: string;
};

export async function schedulePushBroadcast(
  token: string,
  payload: ScheduleBroadcastPayload
): Promise<ScheduleBroadcastResult> {
  return apiCall('/admin/push/schedule', {
    method: 'POST',
    token,
    body: payload,
  });
}

export type AuditLog = {
  id: string;
  action: string;
  userId?: string;
  userEmail?: string;
  details?: string;
  ip?: string;
  userAgent?: string;
  createdAt: string;
};

export async function fetchAuditLogs(
  token: string,
  params: { limit?: number; offset?: number } = {}
): Promise<{
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}> {
  const query = new URLSearchParams();
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  return apiCall(`/admin/audit-logs${qs ? '?' + qs : ''}`, { token });
}

// ─── Push History ────────────────────────────────────

export type PushBroadcast = {
  id: number;
  admin_id: number;
  title: string;
  body: string;
  data: Record<string, unknown>;
  target: string;
  segment: string | null;
  total_users: number;
  total_sent: number;
  total_failed: number;
  created_at: string;
};

export type PushBroadcastsResponse = {
  success: boolean;
  broadcasts: PushBroadcast[];
  total: number;
  limit: number;
  offset: number;
};

export async function fetchPushBroadcasts(
  token: string,
  limit = 50,
  offset = 0
): Promise<PushBroadcastsResponse> {
  return apiCall(`/admin/push/broadcasts?limit=${limit}&offset=${offset}`, { token });
}

// ─── Coin Breakdown (Phase 7E) ─────────────────────

export type CoinBreakdown = {
  total: number;
  mining: number;
  referral: number;
  boost: number;
  task: number;
  bonus: number;
  unclassified: number;
};

export type CoinSource = 'mining' | 'referral' | 'boost' | 'task' | 'bonus';

export async function fetchUserWalletBreakdown(
  token: string,
  userId: string
): Promise<{ success: boolean; breakdown: CoinBreakdown }> {
  return apiCall(`/admin/users/${userId}/wallet-breakdown`, { token });
}

export async function adjustUserBalanceByType(
  token: string,
  userId: string,
  body: { source: CoinSource; delta: number; reason: string }
): Promise<{ success: boolean; before: CoinBreakdown; after: CoinBreakdown }> {
  return apiCall(`/admin/users/${userId}/balance-by-type`, {
    method: 'POST',
    token,
    body,
  });
}

// ─── Tokenomics (Phase 8A) ───────────────────────────

export type CoinSourceName = 'mining' | 'referral' | 'boost' | 'task' | 'bonus';

export type TokenomicsSettings = {
  success: boolean;
  convertibleSources: CoinSourceName[];
  listingEnabled: boolean;
  listingDate: string | null;
  allSources: CoinSourceName[];
};

export async function fetchTokenomicsSettings(
  token: string
): Promise<TokenomicsSettings> {
  return apiCall('/admin/tokenomics/convertible-sources', { token });
}

export async function updateTokenomicsSettings(
  token: string,
  body: {
    convertibleSources: CoinSourceName[];
    listingEnabled: boolean;
    listingDate?: string;
  }
): Promise<{ success: boolean; convertibleSources: CoinSourceName[] }> {
  return apiCall('/admin/tokenomics/convertible-sources', {
    method: 'PUT',
    token,
    body,
  });
}

// ─── Devices (Phase 8B) ────────────────────────────

export type DeviceRow = {
  id: number;
  device_id: string;
  user_id: number;
  email: string | null;
  display_name: string | null;
  platform: string | null;
  model: string | null;
  os_version: string | null;
  ip_address: string | null;
  is_emulator: boolean;
  created_at: string;
  last_seen_at: string;
};

export type DeviceStats = {
  total: number;
  emulators: number;
  unique_devices: number;
  unique_ips: number;
  users_with_device: number;
};

export async function fetchDevices(
  token: string,
  params: { limit?: number; offset?: number; emulator?: boolean; search?: string } = {}
): Promise<{ success: boolean; devices: DeviceRow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.emulator !== undefined) qs.set('emulator', String(params.emulator));
  if (params.search) qs.set('search', params.search);
  return apiCall(`/admin/devices?${qs.toString()}`, { token });
}

export async function fetchDeviceStats(token: string): Promise<{ success: boolean; stats: DeviceStats }> {
  return apiCall('/admin/devices/stats', { token });
}

export async function deleteDevice(token: string, deviceId: number): Promise<{ success: boolean }> {
  return apiCall(`/admin/devices/${deviceId}`, { method: 'DELETE', token });
}

// ─── Referral Tree (Phase 8B) ───────────────────────

export type ReferralNode = {
  id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  created_at: string;
  referral_rewarded: boolean;
};

export type ReferralTreeResponse = {
  success: boolean;
  root: ReferralNode | null;
  tree: {
    level1: ReferralNode[];
    level2: ReferralNode[];
    level3: ReferralNode[];
  };
  stats: {
    totalL1: number;
    totalL2: number;
    totalL3: number;
    total: number;
  };
};

export async function fetchReferralTree(
  token: string,
  userId: string
): Promise<ReferralTreeResponse> {
  return apiCall(`/admin/users/${userId}/referral-tree`, { token });
}
